import React, { ChangeEvent, FC, MouseEvent, useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IPlatform } from '../../../../shared/interfaces/IPlatform';
import { Check } from '@mui/icons-material';
import SurveyContactData from '../../../../components/SurveyContactData/SurveyContactData';
import PlatformService from '../../../../services/PlatformService';
import DesignChanger from '../../../../components/DesignChanger/DesignChanger';
import { ReactComponent as FlagDe } from '../../../../assets/images/flags/de.svg';
import { ReactComponent as FlagGb } from '../../../../assets/images/flags/gb.svg';
import { ReactComponent as FlagNl } from '../../../../assets/images/flags/nl.svg';
import { IDesign } from '../../../../shared/interfaces/IDesign';
import { IContactData } from '../../../../shared/interfaces/IContactData';
import SurveyService from '../../../../services/SurveyService';
import { AuthContext } from '../../../../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { ISurveyStandardEntry } from '../../../../shared/interfaces/ISurveyStandardEntry';
import { ISurvey } from '../../../../shared/interfaces/ISurvey';
import PlatformSelect from '../../../../components/PlatformSelect/PlatformSelect';
import SurveyUtils from '../../../../shared/SurveyUtils';
import StringUtils from '../../../../shared/StringUtils';

const defaultDesign: IDesign = {
  companyLogo: '',
  customFont: '',
  customFontFormat: '',
  customCss: '',
  primaryColor: '#357E8B',
  secondaryColor: '#000000',
};

const defaultContactData = {
  contactEmail: '',
  imprintLink: '',
  privacyPolicyLink: '',
  logoLink: '',
};

const defaultSurveyData: ISurveyStandardEntry = {
  companyName: '',
  companyWebsiteUrl: '',
  contactForm: false,
  customFont: '',
  customFontFormat: '',
  customCss: '',
  label: '',
  lang: 'de',
  note: '',
  outroText: 'Wir bedanken uns für Ihre Anregungen und Hinweise.',
  outroTextNegative: 'Wir bedanken uns für Ihre Anregungen und Hinweise.',
  platform: '',
  platformUrl: '',
  questions: [
    {
      question: '',
      score: 8,
    },
    {
      question: '',
      score: 8,
    },
    {
      question: '',
      score: 8,
    },
  ],
  slug: '',
  title: 'Vielen Dank für Ihre Teilnahme an unserer Umfrage!',
  titleNegative: 'Vielen Dank für Ihre Teilnahme an unserer Umfrage!',
  feedbackActive: false,
  feedbackText: 'Feedback / konstruktive Kritik / Verbesserungsvorschläge',
  feedbackTitle: 'Was hat Sie zu Ihrer Bewertung veranlasst? (optional)',
  answers: [
    {
      answerOne: 'Sehr zufrieden',
      answerTwo: 'Zufrieden',
      answerThree: 'Neutral',
      answerFour: 'Unzufrieden',
      answerFive: 'Sehr unzufrieden',
    },
  ],
  introText: 'Ihre Zufriedenheit ist uns wichtig!',
  type: 'STANDARD',
  shouldShowPrivacyNotice: false,
  privacyNoticeLink: '',
  privacyNoticeText: 'Ich stimme zu, dass meine Daten zu Analysezwecken gespeichert werden.',
};

interface Props {
  id?: string;
  loadSurveyType?: string;
  isEdit?: boolean;
  reload: () => Promise<void>;
  hasAnswers: boolean;
}

const SurveyToolGuide: FC<Props> = ({ id, isEdit, reload, loadSurveyType, hasAnswers }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRole } = useContext(AuthContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [questionKind, setQuestionKind] = useState<string>('');
  const [questionOne, setQuestionOne] = useState<string>(
    defaultSurveyData.questions[0].question || ''
  );
  const [questionTwo, setQuestionTwo] = useState<string>(
    defaultSurveyData.questions[1].question || ''
  );
  const [questionThree, setQuestionThree] = useState<string>(
    defaultSurveyData.questions[2].question || ''
  );
  const [platforms, setPlatforms] = useState<IPlatform[]>([]);
  const [currentSurveyGuideStep, setCurrentSurveyGuideStep] = useState<number>(0);
  const [canEditDesign, setCanEditDesign] = useState<boolean>(false);
  const [showContactData, setShowContactData] = useState<boolean>(defaultSurveyData.contactForm);
  const [initialized, setInitialized] = useState<boolean>(false);

  const [design, setDesign] = useState<IDesign>(defaultDesign);
  const [contactData, setContactData] = useState<IContactData>(defaultContactData);
  const [surveyData, setSurveyData] = useState<ISurveyStandardEntry>(defaultSurveyData);
  const [npsScoreSlider, setNpsScoreSlider] = useState<number>(surveyData.questions[0].score);
  const [showUrlError, setShowUrlError] = useState<boolean>(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [surveyID, setSurveyID] = useState<string>(id || '');

  const handleNextSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep + 1);
  };

  const handlePreviousSurveyGuideStep = () => {
    setCurrentSurveyGuideStep((prevSurveyGuideStep) => prevSurveyGuideStep - 1);
  };

  const getMergedState = (designOverride: IDesign = design): ISurveyStandardEntry => ({
    ...surveyData,
    ...contactData,
    ...designOverride,
    slug: '',
  });
  // DesignChanger's preview draft only ever needs the fields getMergedState already produces,
  // not the full ISurvey shape (plan fact #1).
  const buildPreviewSurvey = getMergedState as unknown as (design: IDesign) => ISurvey;
  const handleAddSurvey = async () => {
    const mergedState: ISurveyStandardEntry = getMergedState();
    if (isEdit) {
      if (!id) return;
      await SurveyService.updateSurvey(id, mergedState);
    } else {
      mergedState.slug = '';
      await SurveyService.addSurvey(mergedState);
    }
    await reload();
  };

  const handleQuestionKindChange = (newQuestionKind: string) => {
    setInitialized(false);
    setQuestionKind(newQuestionKind);
  };

  const handleQuestionChange = (question: string) => (e: ChangeEvent<HTMLInputElement>) => {
    switch (question) {
      case questionOne:
        setQuestionOne(e.target.value);
        break;
      case questionTwo:
        setQuestionTwo(e.target.value);
        break;
      case questionThree:
        setQuestionThree(e.target.value);
        break;
    }
  };

  const handleOutroTextChange = (companyName: string) => {
    const value = surveyData.outroText.replace(/\[Firmenname\]/, companyName || '[Firmenname]');
    setSurveyData((prevState) => ({
      ...prevState,
      outroText: value,
    }));
  };

  useEffect(() => {
    if (questionKind) {
      switch (questionKind) {
        case 'job_survey':
          setQuestionOne('Wie zufrieden waren Sie mit dem Bewerbungsgespräch?');
          setQuestionTwo(
            'Wie zufrieden sind Sie mit der Kommunikation während Ihres Bewerbungsprozesses?'
          );
          setQuestionThree('Würden Sie sich wieder bei uns bewerben?');
          break;
        case 'job_survey_employee':
          setQuestionOne('Wie zufrieden sind Sie insgesamt mit uns als Arbeitgeber?');
          setQuestionTwo('Wie bewerten Sie Ihre Arbeitsbedingungen?');
          setQuestionThree('Würden Sie uns als Arbeitgeber weiterempfehlen?');
          break;
        case 'customersurvey_order_process':
          setQuestionOne('Wie zufrieden sind Sie mit dem Bestellprozess?');
          setQuestionTwo('Wie zufrieden sind Sie mit unserem Kundenservice?');
          setQuestionThree('Würden Sie wieder bei uns einkaufen?');
          break;
        case 'customersurvey_delivery_time':
          setQuestionOne('Wie zufrieden sind Sie mit der Lieferzeit?');
          setQuestionTwo('Wie zufrieden sind Sie mit unserem Kundenservice?');
          setQuestionThree('Würden Sie wieder bei uns bestellen?');
          break;
      }
      setInitialized(true);
    }
  }, [questionKind]);

  useEffect(() => {
    setSurveyData((prevState) => ({
      ...prevState,
      questions: [
        {
          question: questionOne,
          score: 8,
        },
        {
          question: questionTwo,
          score: 8,
        },
        {
          question: questionThree,
          score: 8,
        },
      ],
    }));
  }, [questionOne, questionTwo, questionThree]);

  useEffect(() => {
    const controller = new AbortController();

    const getPlatforms = async (signal: AbortSignal) => {
      const platforms = await PlatformService.getPlatforms(setLoading, signal);
      if (!platforms) return;

      setPlatforms([...platforms, PlatformService.getOthersPseudoPlatform(t)]);
    };

    (async () => await getPlatforms(controller.signal))();

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!hasRole('can-use-survey')) {
      navigate('/');
    }
  }, [hasRole, navigate]);

  useEffect(() => {
    if (!surveyID) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const getSurvey = async () => {
      setLoading(true);
      try {
        const endpointPath = SurveyUtils.getEndpointByType(loadSurveyType);
        const survey = await SurveyService.getSurveyPublic(
          surveyID,
          endpointPath,
          setLoading,
          signal
        );
        if (!survey) {
          setLoading(false);
          return;
        }
        if (survey.type === 'STANDARD') {
          setSurveyData(survey);
        }
        setQuestionOne(survey.questions[0].question);
        setQuestionTwo(survey.questions[1].question);
        setQuestionThree(survey.questions[2].question);
        setDesign({
          companyLogo: survey.companyLogo,
          primaryColor: survey.primaryColor,
          secondaryColor: survey.secondaryColor,
          customFont: survey.customFont,
          customFontFormat: survey.customFontFormat,
          customCss: survey.customCss ?? '',
        });
        setContactData({
          privacyPolicyLink: survey.privacyPolicyLink,
          contactEmail: survey.contactEmail,
          imprintLink: survey.imprintLink,
          logoLink: survey.logoLink,
        });
        setIsCheckboxChecked(survey.feedbackActive);
        setShowContactData(
          survey.imprintLink !== '' || survey.contactEmail !== '' || survey.privacyPolicyLink !== ''
        );
        setCanEditDesign(
          survey.primaryColor !== '#357E8B' ||
            survey.secondaryColor !== '#000000' ||
            survey.companyLogo !== '' ||
            !!survey.customCss
        );
      } catch (error) {
        console.error('Failed to fetch survey:', error);
      } finally {
        setLoading(false);
      }
    };

    getSurvey();

    return () => {
      controller.abort();
    };
  }, [surveyID]);
  const shouldRender = (stepId: string) => {
    const ALWAYS_EDITABLE = ['label', 'comment', 'intro', 'outro'];
    const RENDER_WHEN_FRESH: string[] = ['question_set', 'score', 'answers', 'portal_data'];

    if (!isEdit) {
      return true;
    }

    if (ALWAYS_EDITABLE.includes(stepId)) {
      return true;
    }

    if (!hasAnswers && RENDER_WHEN_FRESH.includes(stepId)) {
      return true;
    }

    return false;
  };

  return (
    <>
      <Box sx={{ maxWidth: '600px' }}>
        <Stepper
          activeStep={currentSurveyGuideStep}
          orientation="vertical"
        >
          {shouldRender('label') && (
            <Step>
              <StepLabel>{t('survey_label')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('survey_name')}
                  placeholder={'z.B.: Musterfirma'}
                  size={'small'}
                  variant="outlined"
                  sx={{ minWidth: '400px' }}
                  autoFocus
                  value={surveyData.label}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      label: e.target.value,
                    }));
                  }}
                />
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Button
                    disabled={!surveyData.label}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('company_data_label') && (
            <Step>
              <StepLabel>{t('company_data_label')}</StepLabel>
              <StepContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 284,
                  }}
                >
                  <TextField
                    label={t('company_name')}
                    size={'small'}
                    variant="outlined"
                    value={surveyData.companyName}
                    placeholder={t('company_name')}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        companyName: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={t('enter_company_url')}
                    size="small"
                    variant="outlined"
                    placeholder={'z.B.: example.com'}
                    multiline
                    maxRows={'2'}
                    value={surveyData.companyWebsiteUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        companyWebsiteUrl: event.target.value,
                      }));
                      setShowUrlError(false);
                    }}
                    error={showUrlError}
                    helperText={showUrlError ? t('url_invalid') : ''}
                    sx={{
                      minWidth: '284px',
                      mt: theme.spacing(2),
                      color: showUrlError ? 'red' : 'inherit',
                    }}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Button
                    disabled={
                      !surveyData.companyName || !surveyData.companyWebsiteUrl || showUrlError
                    }
                    variant="contained"
                    onClick={() => {
                      if (!StringUtils.isValidCompanyUrl(surveyData.companyWebsiteUrl)) {
                        setShowUrlError(true);
                        return;
                      }
                      handleNextSurveyGuideStep();
                      handleOutroTextChange(surveyData.companyName);
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('question_set') && (
            <Step>
              <StepLabel>{t('question_set')}</StepLabel>
              <StepContent>
                <FormControl
                  size="small"
                  sx={{ mb: theme.spacing(2), minWidth: '284px', maxWidth: '284px' }}
                >
                  <InputLabel id="select-label">{t('select')}</InputLabel>
                  <Select
                    labelId="select-label"
                    label={t('select')}
                    value={questionKind}
                    onChange={(e) => handleQuestionKindChange(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>---</em>
                    </MenuItem>
                    <MenuItem value="job_survey">{t('job_survey_applicant')}</MenuItem>
                    <MenuItem value="job_survey_employee">{t('job_survey_employee')}</MenuItem>
                    <MenuItem value="customersurvey_order_process">
                      {t('customersurvey_order_process')}
                    </MenuItem>
                    <MenuItem value="customersurvey_delivery_time">
                      {t('customersurvey_delivery_time')}
                    </MenuItem>
                  </Select>
                  {(questionKind || surveyData.questions[0].question) && (
                    <Box sx={{ minWidth: '284px' }}>
                      <TextField
                        size="small"
                        value={questionOne}
                        multiline
                        maxRows={2}
                        fullWidth
                        sx={{ mt: theme.spacing(1) }}
                        onChange={handleQuestionChange(questionOne)}
                      />
                      <TextField
                        size="small"
                        value={questionTwo}
                        multiline
                        maxRows={2}
                        fullWidth
                        sx={{ mt: theme.spacing(1) }}
                        onChange={handleQuestionChange(questionTwo)}
                      />
                      <TextField
                        size="small"
                        value={questionThree}
                        multiline
                        maxRows={2}
                        fullWidth
                        sx={{ mt: theme.spacing(1) }}
                        onChange={handleQuestionChange(questionThree)}
                      />
                    </Box>
                  )}
                </FormControl>
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    disabled={
                      !questionKind && !surveyData.questions.every((question) => question.question)
                    }
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('comment') && (
            <Step>
              <StepLabel>Score</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Slider
                    marks
                    valueLabelDisplay={'auto'}
                    min={1}
                    max={10}
                    disabled={!shouldRender('score')}
                    value={
                      surveyData.questions[0].score ? surveyData.questions[0].score : npsScoreSlider
                    }
                    size={'small'}
                    sx={{ my: theme.spacing(2) }}
                    onChange={(e, newValue) => {
                      setNpsScoreSlider(newValue as number);
                      setSurveyData((prevState) => ({
                        ...prevState,
                        questions: [
                          {
                            question: questionOne,
                            score: newValue as number,
                          },
                          {
                            question: questionTwo,
                            score: newValue as number,
                          },
                          {
                            question: questionThree,
                            score: newValue as number,
                          },
                        ],
                      }));
                    }}
                  />
                </Box>
                <Typography>
                  {t('enable_comment_field')}
                  <Checkbox
                    checked={isCheckboxChecked}
                    onChange={() => {
                      setIsCheckboxChecked((prevState) => !prevState);
                      setSurveyData((prevState) => ({
                        ...prevState,
                        feedbackActive: !prevState.feedbackActive,
                      }));
                    }}
                  />
                </Typography>
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Button
                    disabled={npsScoreSlider === 0}
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {isCheckboxChecked && shouldRender('comment') && (
            <Step>
              <StepLabel>{`${t('comment')} (${t('optional_configuration')})`} </StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <TextField
                    label={t('comment_field_title')}
                    size={'small'}
                    multiline
                    maxRows={'2'}
                    variant={'outlined'}
                    value={surveyData.feedbackTitle}
                    placeholder={'Was hat Sie zu Ihrer Bewertung veranlasst? (optional)'}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        feedbackTitle: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                  <TextField
                    label={'Kommentarfeld'}
                    size={'medium'}
                    multiline
                    maxRows={'3'}
                    variant={'outlined'}
                    value={surveyData.feedbackText}
                    placeholder={
                      'Inhalt im Kommentarfeld: Feedback, konstruktive Kritik, Verbesserungsvorschläge'
                    }
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        feedbackText: event.target.value,
                      }));
                    }}
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  />
                </Box>
                <Typography>
                  {t('show_privacy_notice')}{' '}
                  <Checkbox
                    checked={surveyData.shouldShowPrivacyNotice}
                    onChange={() => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        shouldShowPrivacyNotice: !prevState.shouldShowPrivacyNotice,
                        privacyNoticeText: prevState.privacyNoticeText
                          ? prevState.privacyNoticeText
                          : defaultSurveyData.privacyNoticeText,
                      }));
                    }}
                  />
                </Typography>

                {surveyData.shouldShowPrivacyNotice && (
                  <>
                    <TextField
                      label={t('survey_privacy_notice')}
                      size={'medium'}
                      multiline
                      maxRows={'3'}
                      variant={'outlined'}
                      value={surveyData.privacyNoticeText}
                      placeholder={t('survey_privacy_notice_placeholder')}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setSurveyData((prevState) => ({
                          ...prevState,
                          privacyNoticeText: event.target.value,
                        }));
                      }}
                      sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                    />
                    <TextField
                      label={t('survey_privacy_notice_link')}
                      size={'medium'}
                      maxRows={'3'}
                      required={true}
                      variant={'outlined'}
                      value={surveyData.privacyNoticeLink}
                      placeholder={t('survey_privacy_notice_link_placeholder')}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setSurveyData((prevState) => ({
                          ...prevState,
                          privacyNoticeLink: event.target.value,
                        }));
                      }}
                      sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                    />
                  </>
                )}
                <Box>
                  <Button
                    disabled={
                      surveyData.shouldShowPrivacyNotice &&
                      (!surveyData.privacyNoticeLink || surveyData.privacyNoticeLink === '')
                    }
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('answers') && (
            <Step>
              <StepLabel>{t('responses')}</StepLabel>
              <StepContent>
                <TextField
                  label={`${t('response')} 1`}
                  size={'small'}
                  multiline
                  maxRows={3}
                  variant={'outlined'}
                  value={surveyData.answers[0].answerOne}
                  placeholder={
                    'z.B.: Sehr zufrieden, zufrieden, neutral, unzufrieden, sehr unzufrieden'
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => {
                      const newState = { ...prevState };
                      if (newState.answers && newState.answers.length > 0) {
                        newState.answers[0] = {
                          ...newState.answers[0],
                          answerOne: event.target.value,
                        };
                      }
                      return newState;
                    });
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={`${t('response')} 2`}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={surveyData.answers[0].answerTwo}
                  placeholder={
                    'z.B.: Sehr zufrieden, zufrieden, neutral, unzufrieden, sehr unzufrieden'
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => {
                      const newState = { ...prevState };
                      if (newState.answers && newState.answers.length > 0) {
                        newState.answers[0] = {
                          ...newState.answers[0],
                          answerTwo: event.target.value,
                        };
                      }
                      return newState;
                    });
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={`${t('response')} 3`}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={surveyData.answers[0].answerThree}
                  placeholder={
                    'z.B.: Sehr zufrieden, zufrieden, neutral, unzufrieden, sehr unzufrieden'
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => {
                      const newState = { ...prevState };
                      if (newState.answers && newState.answers.length > 0) {
                        newState.answers[0] = {
                          ...newState.answers[0],
                          answerThree: event.target.value,
                        };
                      }
                      return newState;
                    });
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={`${t('response')} 4`}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={surveyData.answers[0].answerFour}
                  placeholder={
                    'z.B.: Sehr zufrieden, zufrieden, neutral, unzufrieden, sehr unzufrieden'
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => {
                      const newState = { ...prevState };
                      if (newState.answers && newState.answers.length > 0) {
                        newState.answers[0] = {
                          ...newState.answers[0],
                          answerFour: event.target.value,
                        };
                      }
                      return newState;
                    });
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={`${t('response')} 5`}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={surveyData.answers[0].answerFive}
                  placeholder={
                    'z.B.: Sehr zufrieden, zufrieden, neutral, unzufrieden, sehr unzufrieden'
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => {
                      const newState = { ...prevState };
                      if (newState.answers && newState.answers.length > 0) {
                        newState.answers[0] = {
                          ...newState.answers[0],
                          answerFive: event.target.value,
                        };
                      }
                      return newState;
                    });
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('intro') && (
            <Step>
              <StepLabel>{'Intro'}</StepLabel>
              <StepContent>
                <TextField
                  label={'Intro'}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={surveyData.introText}
                  placeholder={'Bitte teilen Sie uns Ihre aktuelle Stimmung mit.'}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      introText: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('outro') && (
            <Step>
              <StepLabel>{t('title_and_outro')}</StepLabel>
              <StepContent>
                <TextField
                  label={t('title_if_positive')}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={surveyData.title}
                  placeholder={t('please_share_placeholder')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      title: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={t('outro_if_positive')}
                  size={'small'}
                  multiline
                  maxRows={'6'}
                  variant={'outlined'}
                  value={surveyData.outroText}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      outroText: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <TextField
                  label={t('title_if_negative')}
                  size={'small'}
                  multiline
                  maxRows={'3'}
                  variant={'outlined'}
                  value={surveyData.titleNegative}
                  placeholder={t('please_share_placeholder')}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      titleNegative: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(4) }}
                />
                <TextField
                  label={t('outro_if_negative')}
                  size={'small'}
                  multiline
                  maxRows={'6'}
                  variant={'outlined'}
                  value={surveyData.outroTextNegative}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      outroTextNegative: event.target.value,
                    }));
                  }}
                  sx={{ minWidth: '400px', mt: theme.spacing(2) }}
                />
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          {shouldRender('portal_data') && (
            <Step>
              <StepLabel>{`${t('portal_data')} (${t('optional_configuration')})`}</StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 284 }}>
                  <PlatformSelect
                    value={surveyData.platform}
                    onChange={(event: SelectChangeEvent) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        platform: event.target.value,
                      }));
                    }}
                    platforms={platforms}
                  />
                  <TextField
                    label={`${t('portal_data')}-URL`}
                    size="small"
                    variant="outlined"
                    placeholder={'z.B.: https://www.kununu.de/bewertungen'}
                    value={surveyData.platformUrl}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setSurveyData((prevState) => ({
                        ...prevState,
                        platformUrl: event.target.value,
                      }));
                      setShowUrlError(false);
                    }}
                    error={showUrlError}
                    helperText={showUrlError ? t('url_invalid') : ''}
                    sx={{ mt: theme.spacing(2), color: showUrlError ? 'red' : 'inherit' }}
                    fullWidth
                  />
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (
                        !StringUtils.isValidPlatformUrl(surveyData.platformUrl) &&
                        surveyData.platformUrl !== ''
                      ) {
                        setShowUrlError(true);
                        return;
                      }
                      handleNextSurveyGuideStep();
                    }}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('next')}
                  </Button>
                  <Button
                    disabled={false}
                    variant="contained"
                    color="warning"
                    onClick={handlePreviousSurveyGuideStep}
                    sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          )}
          <Step>
            <StepLabel>{t('finish_configuration')}</StepLabel>
            <StepContent>
              <Box sx={{ mb: theme.spacing(2) }}>
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                  onClick={handleAddSurvey}
                >
                  {t('finish')}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handlePreviousSurveyGuideStep}
                  sx={{ mt: theme.spacing(2), mr: theme.spacing(1) }}
                >
                  {t('back')}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Box>
      {!isEdit ? (
        <>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: theme.spacing(1),
              }}
            >
              <Typography>{t('language')}</Typography>
              <ToggleButtonGroup
                color="primary"
                size="small"
                value={surveyData.lang}
                exclusive
                onChange={(event: MouseEvent<HTMLElement>, value: string) => {
                  if (value) {
                    setSurveyData((prevState) => ({
                      ...prevState,
                      lang: value,
                    }));
                  }
                }}
                sx={{
                  '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                    border: `1px solid ${theme.palette.primary.main}`,
                  },
                }}
              >
                <ToggleButton value="de">
                  <FlagDe
                    width="32px"
                    height="100%"
                  />
                </ToggleButton>
                <ToggleButton value="en">
                  <FlagGb
                    width="32px"
                    height="100%"
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />
                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                    surveyType="STANDARD"
                    buildPreviewSurvey={buildPreviewSurvey}
                    savedSurveyId={surveyID}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                contactForm={showContactData}
                logoLink={contactData.logoLink}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', minHeight: '300px', mx: theme.spacing(6) }}>
            <Divider
              orientation="vertical"
              flexItem
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignSelf: 'flex-start',
              minWidth: '400px',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: theme.spacing(2) }}
            >
              {t('optional_configuration')}
            </Typography>
            <FormGroup>
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: theme.spacing(1),
                  }}
                >
                  <Typography>{t('language')}</Typography>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={surveyData.lang}
                    exclusive
                    onChange={(event: MouseEvent<HTMLElement>, value: string | null) => {
                      if (value) {
                        setSurveyData((prevState) => ({
                          ...prevState,
                          lang: value,
                        }));
                      }
                    }}
                    sx={{
                      '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                        border: `1px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ToggleButton value="de">
                      <FlagDe
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                    <ToggleButton value="en">
                      <FlagGb
                        width="32px"
                        height="100%"
                      />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={canEditDesign}
                      onChange={() => {
                        setCanEditDesign((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">Design</Typography>}
                />

                {canEditDesign && (
                  <DesignChanger
                    defaultDesign={design}
                    onChange={setDesign}
                    surveyType="STANDARD"
                    buildPreviewSurvey={buildPreviewSurvey}
                    savedSurveyId={surveyID}
                  />
                )}
                <FormControlLabel
                  sx={{ mt: theme.spacing(2) }}
                  control={
                    <Switch
                      checked={showContactData}
                      onChange={() => {
                        setShowContactData((prevState) => !prevState);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{t('contact_data')}</Typography>}
                />
              </Box>
            </FormGroup>
            {showContactData && (
              <SurveyContactData
                privacyPolicyLink={contactData.privacyPolicyLink}
                contactEmail={contactData.contactEmail}
                imprintLink={contactData.imprintLink}
                logoLink={contactData.logoLink}
                contactForm={showContactData}
                onChange={setContactData}
              />
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default SurveyToolGuide;
